import { useNavigate } from '@tanstack/react-router'
import {
  FACILITY_MODEL_EVIDENCE_OPEN,
  FACILITY_MODEL_SUBJECT_OPEN,
  FACILITY_MODEL_TREND_OPEN,
  useGeckoAnalytics,
} from 'features/analytics'
import {
  chartResourceKey,
  sentinelResourceKey,
  subjectResourceKey,
  trendResourceKey,
} from 'features/facilityModel/resourceKeys'
import type { NormalizedBBox } from 'features/fimEvidence'
import { serializeSelectedRegionsParam } from 'features/knowledgeDocumentViewer/utils/selectedRegionsParam'
import { SelectedTab } from 'features/navbar/types'
import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useCallback } from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { CLEAR_SEARCH_VIEWER_PARAMS } from 'routes/documents/-searchParams'
import {
  SEARCH_PARAM_END,
  SEARCH_PARAM_SENSOR_IDS,
  SEARCH_PARAM_START,
} from 'routes/process-data/-searchParams'

const siteQuery = (siteId: string | null | undefined): string =>
  siteId ? `?${SEARCH_PARAM_SITE_ID}=${encodeURIComponent(siteId)}` : ''

export const subjectDetailHref = (
  subjectUid: string,
  siteId: string | null | undefined
): string =>
  `/facility-model/subjects/${encodeURIComponent(subjectUid)}${siteQuery(siteId)}`

export const sentinelDetailHref = (
  entryId: string,
  siteId: string | null | undefined
): string =>
  `/facility-model/sentinels/${encodeURIComponent(entryId)}${siteQuery(siteId)}`

const TREND_WINDOW_DAYS = 30

export interface TrendWindow {
  start: string
  end: string
}

export const defaultTrendWindow = (now: Date = new Date()): TrendWindow => ({
  start: new Date(
    now.getTime() - TREND_WINDOW_DAYS * 24 * 3600_000
  ).toISOString(),
  end: now.toISOString(),
})

export const trendHref = (
  siteId: string,
  sensorIds: readonly string[],
  window: TrendWindow
): string =>
  `/process-data/view?${SEARCH_PARAM_SITE_ID}=${encodeURIComponent(siteId)}` +
  `&${SEARCH_PARAM_SENSOR_IDS}=${encodeURIComponent(sensorIds.join(','))}` +
  `&${SEARCH_PARAM_START}=${encodeURIComponent(window.start)}` +
  `&${SEARCH_PARAM_END}=${encodeURIComponent(window.end)}`

const orgQuery = (orgId: string | null | undefined): string =>
  orgId ? `&${SEARCH_PARAM_ORG_ID}=${encodeURIComponent(orgId)}` : ''

export const savedChartHref = (
  siteId: string,
  chartId: string,
  orgId?: string | null
): string =>
  `/process-data/chart/${encodeURIComponent(chartId)}` +
  `?${SEARCH_PARAM_SITE_ID}=${encodeURIComponent(siteId)}${orgQuery(orgId)}`

export const NEW_VIEW_LABEL = 'New view'

export const newProcessDataViewHref = (
  siteId: string,
  orgId?: string | null
): string =>
  `/process-data/view?${SEARCH_PARAM_SITE_ID}=${encodeURIComponent(siteId)}` +
  orgQuery(orgId)

/**
 * Opening a Subject from anywhere in Facility Model. In the workspace layout
 * the catalog's landing tab is preserved and the Subject gets its own
 * resource tab keyed by `subject:<uid>`; the legacy layout navigates the page
 * to the same route.
 */
export const useOpenSubjectResource = (siteId: string | null | undefined) => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()
  const { trackEvent } = useGeckoAnalytics()

  return useCallback(
    (subjectUid: string, subjectName: string) => {
      trackEvent(FACILITY_MODEL_SUBJECT_OPEN.title, {
        subjectUid,
        workspace: !!actions,
      })
      if (actions) {
        actions.openResourceTab({
          resourceKey: subjectResourceKey(subjectUid),
          workspaceDestination: SelectedTab.FacilityModel,
          label: subjectName,
          href: subjectDetailHref(subjectUid, siteId),
        })
        return
      }
      void navigate({
        to: '/facility-model/subjects/$subjectUid',
        params: { subjectUid },
        search: { [SEARCH_PARAM_SITE_ID]: siteId ?? undefined },
      })
    },
    [actions, navigate, siteId, trackEvent]
  )
}

/** Every surface that can open Process Data, for analytics attribution. */
export type TrendSurface =
  | 'facility-model'
  | 'model-operations'
  | 'document-viewer'
  | 'doc-search'
  | 'chatbot'

/**
 * Opening a Sentinel from anywhere in Facility Model — same contract as
 * `useOpenSubjectResource`, keyed by `sentinel:<entry id>`.
 */
const FACILITY_MODEL_SENTINEL_OPEN = {
  title: 'Facility Model Sentinel Opened',
  description: 'User opened a Sentinel resource tab from a subject or a link',
}

export const useOpenSentinelResource = (siteId: string | null | undefined) => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()
  const { trackEvent } = useGeckoAnalytics()

  return useCallback(
    (entryId: string, sentinelName: string) => {
      trackEvent(FACILITY_MODEL_SENTINEL_OPEN.title, {
        entryId,
        workspace: !!actions,
      })
      if (actions) {
        actions.openResourceTab({
          resourceKey: sentinelResourceKey(entryId),
          workspaceDestination: SelectedTab.FacilityModel,
          label: sentinelName,
          href: sentinelDetailHref(entryId, siteId),
        })
        return
      }
      void navigate({
        to: '/facility-model/sentinels/$sentinelId',
        params: { sentinelId: entryId },
        search: { [SEARCH_PARAM_SITE_ID]: siteId ?? undefined },
      })
    },
    [actions, navigate, siteId, trackEvent]
  )
}

/**
 * Explicit "open trend" for a set of resolved datapoint ids, defaulting to a
 * 30-day window. In the workspace layout the trend gets its own Process Data
 * resource tab keyed by the sensor set, so re-opening the same tags focuses
 * the existing chart. `surface` tags analytics with the opening page.
 *
 * This is the only way to open a chart for a set of sensors. The document
 * viewer's embedded chart is not a substitute — it is a different component
 * with a different feature set, which is how sensor links drifted onto a
 * second Process Data implementation (POWER-152).
 */
export const useOpenTrendResource = (
  siteId: string | null | undefined,
  surface: TrendSurface = 'facility-model'
) => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()
  const { trackEvent } = useGeckoAnalytics()

  return useCallback(
    (
      sensorIds: readonly string[],
      label: string,
      timeWindow?: { from: string; to: string }
    ) => {
      if (!siteId || sensorIds.length === 0) return
      trackEvent(FACILITY_MODEL_TREND_OPEN.title, {
        sensorCount: sensorIds.length,
        workspace: !!actions,
        surface,
      })
      const window = timeWindow
        ? { start: timeWindow.from, end: timeWindow.to }
        : defaultTrendWindow()
      if (actions) {
        actions.openResourceTab({
          resourceKey: trendResourceKey(siteId, sensorIds),
          workspaceDestination: SelectedTab.ProcessData,
          label,
          href: trendHref(siteId, sensorIds, window),
        })
        return
      }
      void navigate({
        to: '/process-data/view',
        search: {
          [SEARCH_PARAM_SITE_ID]: siteId,
          [SEARCH_PARAM_SENSOR_IDS]: sensorIds.join(','),
          [SEARCH_PARAM_START]: window.start,
          [SEARCH_PARAM_END]: window.end,
        },
      })
    },
    [actions, navigate, siteId, surface, trackEvent]
  )
}

/**
 * Open a SAVED chart in the workbench, keyed by chart id so a second open
 * focuses the live editor rather than mounting a duplicate that would fight
 * the first one's saves.
 */
export const useOpenSavedChartResource = (
  siteId: string | null | undefined,
  orgId?: string | null
) => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()

  return useCallback(
    (chartId: string, label: string) => {
      if (!siteId) return
      if (actions) {
        actions.openResourceTab({
          resourceKey: chartResourceKey(chartId),
          workspaceDestination: SelectedTab.ProcessData,
          label,
          href: savedChartHref(siteId, chartId, orgId),
        })
        return
      }
      void navigate({
        to: '/process-data/chart/$chartID',
        params: { chartID: chartId },
        search: {
          [SEARCH_PARAM_SITE_ID]: siteId,
          ...(orgId ? { [SEARCH_PARAM_ORG_ID]: orgId } : {}),
        },
      })
    },
    [actions, navigate, orgId, siteId]
  )
}

/**
 * Open a blank Process Data view. An unseeded ad-hoc view is a "new view" to
 * the workbench, which forces the rails open with the Tags pane expanded —
 * the tag-search entry point the legacy embedded tab spelled with its own
 * `autoOpenTagSearch` flag.
 */
export const useOpenNewProcessDataView = (
  siteId: string | null | undefined,
  orgId?: string | null
) => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()

  return useCallback(() => {
    if (!siteId) return
    if (actions) {
      actions.openDestinationTab({
        destinationKey: SelectedTab.ProcessData,
        label: NEW_VIEW_LABEL,
        href: newProcessDataViewHref(siteId, orgId),
      })
      return
    }
    void navigate({
      to: '/process-data/view',
      search: {
        [SEARCH_PARAM_SITE_ID]: siteId,
        ...(orgId ? { [SEARCH_PARAM_ORG_ID]: orgId } : {}),
      },
    })
  }, [actions, navigate, orgId, siteId])
}

export interface OpenCitationOptions {
  documentId: string
  page?: number
  boxes?: NormalizedBBox[]
  label?: string
  fragmentText?: string
}

/** Explicit citation open. Nothing opens a document implicitly. */
export const useOpenCitationDocument = (siteId: string | null | undefined) => {
  const actions = useOptionalWorkspaceTabsActions()
  const navigate = useNavigate()
  const { trackEvent } = useGeckoAnalytics()

  return useCallback(
    ({ documentId, page, boxes, label, fragmentText }: OpenCitationOptions) => {
      trackEvent(FACILITY_MODEL_EVIDENCE_OPEN.title, {
        documentId,
        page,
        workspace: !!actions,
      })
      if (actions) {
        actions.openDocumentTab({
          documentId,
          pageNumber: page,
          label,
          ...(page !== undefined &&
            boxes !== undefined &&
            boxes.length > 0 && { regionHighlight: { pageNum: page, boxes } }),
          ...(page !== undefined &&
            fragmentText && {
              fragmentHighlight: { pageNum: page, fragmentText },
            }),
        })
        return
      }
      void navigate({
        to: '/documents',
        search: {
          ...CLEAR_SEARCH_VIEWER_PARAMS,
          [SEARCH_PARAM_SITE_ID]: siteId ?? undefined,
          documentID: documentId,
          pageNumber: page,
          selectedRegions: serializeSelectedRegionsParam(boxes ?? []),
        },
      })
    },
    [actions, navigate, siteId, trackEvent]
  )
}
